import graphqlClient from "@/GQLClient";
import {
  type EntityPagerFilter,
  Delete_Type,
  GetSummarizedMealsOnMyInstitutionDocument,
  type MealUnion,
  GetInternalMealsOnMyInstitutionDocument,
  type MealInput,
  SaveMealDocument,
  DeleteMealDocument,
  GetSummarizedExternalMealsDocument,
  Module_Type,
  GetExternalMealsOnMyInstitutionDocument,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getMeals = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<MealUnion>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedMealsOnMyInstitutionDocument,
    variables: {
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.meals) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.meals;
    return {
      edges: edges.map((item) => item.node as MealUnion),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getMealDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalMealsOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.meals) {
    const { edges } = response.data.institutions.getMyInstitution.meals;
    return edges.map((item) => item.node as MealUnion).at(0);
  }

  throw new SentryGraphQLError();
};

const getExternalMeals = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<MealUnion>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalMealsDocument,
    variables: {
      filter,
      type: Module_Type.Meal,
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as MealUnion),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getExternalMealDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalMealsOnMyInstitutionDocument,
    variables: {
      type: Module_Type.Meal,
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges.map((item) => item.node as MealUnion).at(0);
  }

  throw new SentryGraphQLError();
};

const save = async (meal: MealInput, dishes: string[]) => {
  const response = await graphqlClient.mutate({
    mutation: SaveMealDocument,
    variables: {
      meal,
      dishes,
    },
  });

  if (response.data?.meal?.saveMeal.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteMealDocument,
    variables: {
      id,
      type,
    },
  });

  if (response.data?.meal?.deleteMeal?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

export default {
  getMeals,
  getMealDetail,
  getExternalMeals,
  getExternalMealDetail,
  save,
  deleteItem,
};
